import type { FC } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import { N800 } from '@atlaskit/theme/colors';

import { CONTEXT_PATH, SPACE_OVERVIEW } from '@confluence/named-routes';
import { Link } from '@confluence/route-manager';
import { TimeAgo } from '@confluence/time-ago';
import { fg } from '@confluence/feature-gating';

import { i18n } from '../i18n';
import { NarrowPageCardLayout } from '../layouts/NarrowPageCardLayout';

import { StatusLozenge } from './StatusLozenge';
import { EmojiContentIcon, getEmoji, getTitle } from './EmojiContentIcon';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceLinkWrapper = styled.div({
	zIndex: 1,
	display: 'flex',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceLink = styled(Link)({
	position: 'relative',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LastModifiedWrapper = styled.div({
	font: token('font.body.small'),
	verticalAlign: 'baseline',
});

const titleSelector = 'home.recents.Title';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndStatus = styled.div({
	display: 'flex',
	/* CSS specificity fix. turns default blue if it goes directly in the a tag */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`> [data-styled-selector="${titleSelector}"]`]: {
		color: token('color.text', N800),
		'&:hover': {
			color: token('color.text.subtle', N800),
			textDecoration: 'none',
		},
		'&:focus, &:focus-visible': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			outlineOffset: '-1px',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.a<{ 'data-styled-selector'?: string }>({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	position: 'relative',
	whiteSpace: 'nowrap',
	color: token('color.text', N800),
});
Title.defaultProps = {
	'data-styled-selector': titleSelector,
};

type ActivitiesQueryRecentType = {
	object: {
		content: {
			status: string | null;
			space: { key: string | null; name: string | null } | null;
			links: { webui: string | null } | null;
			type: string | null;
			subType: string | null;
			title: string | null;
		} | null;
	} | null;
	timestamp: any;
};

type NarrowPageCardViewProps = {
	recent: ActivitiesQueryRecentType;
	onClick?: () => void;
	onSpaceClick?: () => void;
	action?: string;
	lastActionI18n?: MessageDescriptor;
};

export const NarrowPageCardView: FC<NarrowPageCardViewProps> = ({
	recent,
	onClick,
	onSpaceClick,
	action,
	lastActionI18n,
}) => {
	const content = recent.object?.content;

	const EMOJI_CONTENT_TYPES = fg('confluence_frontend_whiteboard_emoji_titles')
		? ['page', 'blogpost', 'embed', 'whiteboard']
		: ['page', 'blogpost', 'embed'];
	const url = `${CONTEXT_PATH}${content?.links?.webui}`;

	const isEmojiContentType = content?.type && EMOJI_CONTENT_TYPES.includes(content.type);
	const { emoji = undefined, hasEmojiProperty = undefined } = isEmojiContentType
		? getEmoji(content)
		: {};
	const title = isEmojiContentType ? getTitle(content?.title, hasEmojiProperty) : content?.title;

	const icon = (
		<EmojiContentIcon
			emoji={emoji}
			contentType={content?.type || ''}
			subType={content?.subType || ''}
			removeMargin={false}
		/>
	);

	const primary = (
		<TitleAndStatus>
			<Tooltip content={title || <FormattedMessage {...i18n.contentUntitled} />} position="bottom">
				{(tooltipProps) => (
					<Title href={url} {...tooltipProps}>
						{title || <FormattedMessage {...i18n.contentUntitled} />}
					</Title>
				)}
			</Tooltip>
			<StatusLozenge status={content?.status || ''} />
		</TitleAndStatus>
	);

	const space = content?.space;
	const spaceKey = space?.key;
	const spaceName = space?.name || '';
	const secondary =
		spaceName &&
		(spaceKey ? (
			<SpaceLinkWrapper>
				<SpaceLink href={SPACE_OVERVIEW.toUrl({ spaceKey })} onClick={onSpaceClick}>
					{spaceName}
				</SpaceLink>
			</SpaceLinkWrapper>
		) : (
			spaceName
		));
	const lastModifiedComponent = (
		<LastModifiedWrapper>
			{lastActionI18n && (
				<Fragment>
					<FormattedMessage {...lastActionI18n} />{' '}
				</Fragment>
			)}
			<TimeAgo date={recent.timestamp} />
		</LastModifiedWrapper>
	);
	return (
		<NarrowPageCardLayout
			icon={icon}
			primary={primary}
			secondary={secondary}
			lastModified={lastModifiedComponent}
			actions={action}
			url={url}
			onClick={onClick}
			ariaLabel={title || <FormattedMessage {...i18n.contentUntitled} />}
			testId="narrow-page-card"
		/>
	);
};
